// © Microsoft Corporation. All rights reserved.

import { UrlSearchParamsHelper, UrlUtility } from "../utilities";
import { canUseDOM, getLocationHost, getLocationSearch } from "@msnews/isomorphic-util";

import { headData } from "../app/HeadData";

// See https://webpack.js.org/guides/public-path/#on-the-fly for the details of __webpack_public_path__
declare let __webpack_public_path__: string;

// This is the statics path defined on server side.
declare let __WEBPACK_LOCAL_STATICS_PATH__: string;

const prodAssets = "//assets.msn.com";
const cnProdAssets = "//assets.msn.cn";

const localHost = "localhost.msn.com:8080";

const renderWidgetQS = "renderWidget";

const ENV = {
    "int.msn.com": prodAssets,
    "int.msn.cn": cnProdAssets,
    "int1.msn.com": prodAssets,
    "int1.msn.cn": cnProdAssets,
    "localhost:6000": prodAssets, // for pcs local dev box,
    "localhost.msn.com:8080": `//${localHost}`,
    "localhost.windows.msn.com": prodAssets,
    "microsoftnews-dev.msn.com": prodAssets,
    "microsoftnews-int.msn.com": prodAssets,
    "microsoftnews-int.msn.cn": cnProdAssets,
    "microsoftnews-int1.msn.com": prodAssets,
    "microsoftnews-int1.msn.cn": cnProdAssets,
    "microsoftnews.msn.com": prodAssets,
    "microsoftnews.msn.cn": cnProdAssets,
    "msnrender-sf-sand-westus.msn.com:33000": prodAssets,
    "msnrender-sf-sand-eastus.msn.com:33000": prodAssets,
    "ntp.msn.com": prodAssets,
    "ntp.msn.cn": cnProdAssets,
    "owa-int.msn.com": prodAssets,
    "owa-int.msn.cn": cnProdAssets,
    "owa-int1.msn.com": prodAssets,
    "owa-int1.msn.cn": cnProdAssets,
    "owa.msn.com": prodAssets,
    "owa.msn.cn": cnProdAssets,
    "pagecomposition.msn.com": prodAssets,
    "pagecomposition-int.msn.com": prodAssets,
    "superapp-int.msn.com": prodAssets,
    "superapp-int.msn.cn": cnProdAssets,
    "superapp-int1.msn.cn": cnProdAssets,
    "superapp-int1.msn.com": prodAssets,
    "superapp.msn.cn": cnProdAssets,
    "superapp.msn.com": prodAssets,
    "windows-int.msn.com": prodAssets,
    "windows-int.msn.cn": cnProdAssets,
    "windows-int1.msn.com": prodAssets,
    "windows-int1.msn.cn": cnProdAssets,
    "windows.msn.com": prodAssets,
    "windows.msn.cn": cnProdAssets,
    "www.msn.com": prodAssets,
    "www.msn.cn": cnProdAssets
};

export const getPublicPath = (locationSearch: string, locationHost: string, webpackPublicPath: string): string => {
    const search = new URLSearchParams(locationSearch);

    // Special arrangement for local render service server
    // since it could support either assets.msn.com or local static path.
    // Extend logic for rendering individual experiences (widgets)
    const renderWidgets = search.has(renderWidgetQS) && search.get(renderWidgetQS) === "true";
    const isLocalHost = locationHost === localHost || renderWidgets;
    const useDeployedBundles = search.has("useDeployedBundles") && search.get("useDeployedBundles") === "true";
    const assetHost: string = (isLocalHost && useDeployedBundles) ? prodAssets : ENV[locationHost];

    if (search.has("entry")) {
        const segments = search.get("entry").split("/").slice(0, -1).join("/") + "/";

        if (assetHost) {
            return assetHost + segments;
        }
    } else if (assetHost && isLocalHost && !useDeployedBundles) {

        if (!canUseDOM()) {
            // Use direct static path on local dev server.
            const path = __WEBPACK_LOCAL_STATICS_PATH__;
            return assetHost + path;
        } else if (webpackPublicPath) {
            return assetHost + webpackPublicPath;
        } else {
            return webpackPublicPath;
        }
    } else {
        if (assetHost) {

            // Combine query string params from HeadData with current query string param
            // to figure out what is the supported qsp is.
            // This is to solve qsp rewritten to PCS but client side have no qsp in address.
            const headDataQsp = headData.ClientSettings && headData.ClientSettings.queryparams;
            const searchParamStringLocation = (locationSearch || "").substring(1);
            const searchParamStringHeadData = (headDataQsp || "").substring(1);

            // Current query string param from client side has higher priority
            let combinedString = "?" + searchParamStringHeadData ? searchParamStringLocation + "&" + searchParamStringHeadData : searchParamStringLocation;
            combinedString = decodeURIComponent(combinedString);

            // For duplicate qsp keys, UrlSearchParamsHelper assumes first given, and other duplicates are ignored.
            const search = new UrlSearchParamsHelper(combinedString);
            const bundlesValue = search.get("bundles");

            // Item qsp supports multiple items. First value of the same key will be used.
            // Example, spalinkValue is rolling when qsp is item=test&item=spalink:rolling&item=spalink:123.45
            const itemParams = UrlUtility.getParamsWithItems(combinedString);
            const spalinkItem = itemParams.find(item => item.key === "spalink");

            // Take care of potential spalink:/rolling
            let spalinkValues = spalinkItem && spalinkItem.value && spalinkItem.value.split("/");
            if (spalinkValues && spalinkValues[0] === "") {
                spalinkValues = spalinkValues.slice(1);
            }
            const spalinkDir = spalinkValues && spalinkValues[0];

            if (webpackPublicPath) {

                // in order to have the lazy chunk pointing to latest with a stable path, the build number will be overridden on runtime
                // PATHNAME: /bundles/v1/mmx/buildNumber/
                // PATHNAME.split("/"): ["", "bundles", "v1", "mmx", "buildNumber", ""]
                //
                // Example,
                // Query string param: item=spalink:123.45/debug
                // webpackPublicPath before: /bundles/v1/mmx/buildNumber/debug
                // webpackPublicPath after: /bundles/v1/mmx/123.45/debug
                const segments = webpackPublicPath.split("/");
                if (spalinkDir) {
                    segments[4] = spalinkDir;
                } else if (bundlesValue) {
                    segments[4] = bundlesValue;
                } else {
                    segments[4] = "latest";
                }
                return assetHost + segments.join("/");
            }
        }
    }

    return webpackPublicPath;
};

export const initializePublicPath = () => {
    __webpack_public_path__ = getPublicPath(getLocationSearch(), getLocationHost(), __webpack_public_path__);
};

export const initializeHybridPublicPath = () => {
    const search = decodeURIComponent(window.location.search);
    if (search.indexOf("item=spa_env:localdev") > -1) {
        // Use active prime instance SPA directory for localdev
        __webpack_public_path__ = `//${window.location.host}/spa/`;
        return;
    }

    const host = ENV[window.location.host] || prodAssets;

    // Item qsp supports multiple items. First value of the same key will be used.
    // Example, spalinkValue is rolling when qsp is item=test&item=spalink:rolling&item=spalink:123.45
    const itemParams = UrlUtility.getParamsWithItems(search);
    const spalinkItem = itemParams.find(item => item.key === "spalink");

    // Take care of potential spalink:/rolling
    let spalinkValues = spalinkItem && spalinkItem.value && spalinkItem.value.split("/");
    if (spalinkValues && spalinkValues[0] === "") {
        spalinkValues = spalinkValues.slice(1);
    }
    const spalinkDir = spalinkValues && spalinkValues[0];

    // in order to have the lazy chunk pointing to latest with a stable path, the build number will be overridden on runtime
    // PATHNAME: /bundles/v1/mmx/buildNumber/
    // PATHNAME.split("/"): ["", "bundles", "v1", "mmx", "buildNumber", ""]
    //
    // Example,
    // Query string param: item=spalink:123.45/debug
    // webpackPublicPath before: /bundles/v1/mmx/buildNumber/debug
    // webpackPublicPath after: /bundles/v1/mmx/123.45/debug
    const segments = __webpack_public_path__.split("/");
    segments[4] = (spalinkDir) ? spalinkDir : "latest";
    __webpack_public_path__ = host + segments.join("/");
};
